<template>
    <div class="space-y-4">
        <project-change-notice
            :project-key="projectKey"
            @refresh="refreshStats"
        />
        <dl
            class="grid grid-cols-1 gap-px overflow-hidden rounded-xl bg-taupe-50 ring-1 ring-taupe-200 sm:grid-cols-2 md:grid-cols-4"
            data-testid="flag-stats-panel"
        >
            <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                <dt
                    class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                >
                    Last Retrieved
                </dt>
                <dd
                    class="mt-1.5 flex items-baseline justify-between md:block lg:flex"
                >
                    <div
                        class="text-sm font-medium text-taupe-800"
                        data-testid="stat-value-last-retrieved"
                    >
                        <zen-skeleton
                            v-if="loading.lastRetrieval"
                            class="h-4 w-20"
                        />
                        <div
                            v-if="
                                !loading.lastRetrieval &&
                                ((lastRetrieval != undefined &&
                                    lastRetrieval.last_retrieved_at ==
                                        undefined) ||
                                    lastRetrieval == undefined)
                            "
                        >
                            Never
                        </div>
                        <div
                            v-if="
                                !loading.lastRetrieval &&
                                lastRetrieval != undefined &&
                                lastRetrieval.last_retrieved_at != undefined
                            "
                        >
                            <zen-date-time
                                :value="lastRetrieval.last_retrieved_at"
                            />
                        </div>
                    </div>
                </dd>
            </div>
            <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                <dt
                    class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                >
                    Last Used
                </dt>
                <dd class="mt-1.5">
                    <div class="text-sm font-medium text-taupe-800">
                        <zen-skeleton v-if="loading.lastUse" class="h-4 w-20" />
                        <div
                            v-if="
                                !loading.lastUse &&
                                (lastUse == undefined ||
                                    lastUse.created_at == undefined)
                            "
                        >
                            Never
                        </div>
                        <div
                            v-if="
                                !loading.lastUse &&
                                lastUse != undefined &&
                                lastUse.created_at != undefined
                            "
                        >
                            <zen-date-time :value="lastUse.created_at" />
                        </div>
                    </div>
                    <div
                        v-if="
                            !loading.evaluations && evaluationSeries.length > 0
                        "
                        ref="evalBarRef"
                        class="mt-2.5"
                        data-testid="flag-evaluation-bar"
                        @mouseenter="showEvalTooltip"
                        @mouseleave="hideEvalTooltip"
                    >
                        <div
                            class="flex h-1.5 w-full cursor-default overflow-hidden rounded-full bg-taupe-100"
                        >
                            <div
                                v-for="s in evaluationSeries"
                                :key="s.key"
                                :style="{
                                    width: evaluationPercent(s) + '%',
                                    backgroundColor: s.color,
                                }"
                                class="h-full transition-all"
                            ></div>
                        </div>
                        <Teleport to="body">
                            <div
                                v-if="evalTooltip.visible"
                                ref="evalTooltipRef"
                                :style="evalTooltip.style"
                                class="pointer-events-none z-50 w-48 rounded-md bg-taupe-500 p-2.5 text-white shadow-lg"
                                data-testid="flag-evaluation-bar-tooltip"
                            >
                                <div class="space-y-1">
                                    <div
                                        v-for="s in evaluationSeries"
                                        :key="s.key"
                                        class="flex items-center justify-between gap-2 text-xs"
                                    >
                                        <span
                                            class="inline-flex items-center gap-1.5 truncate"
                                        >
                                            <span
                                                class="h-2 w-2 shrink-0 rounded-full"
                                                :style="{
                                                    backgroundColor: s.color,
                                                }"
                                            ></span>
                                            <span class="truncate">{{
                                                s.label
                                            }}</span>
                                        </span>
                                        <span class="shrink-0 tabular-nums"
                                            >{{ evaluationPercent(s) }}%</span
                                        >
                                    </div>
                                </div>
                                <div
                                    class="mt-2 border-t border-taupe-400 pt-1.5 text-xs text-taupe-200"
                                >
                                    {{ evaluationTotal.toLocaleString() }} evals
                                    (30d)
                                </div>
                                <div
                                    ref="evalArrowRef"
                                    class="absolute h-2 w-2 rotate-45 bg-taupe-500"
                                    :style="evalTooltip.arrowStyle"
                                ></div>
                            </div>
                        </Teleport>
                    </div>
                    <div
                        v-else-if="!loading.evaluations && evaluationTotal > 0"
                        class="mt-1.5 text-xs text-taupe-400"
                    >
                        {{ evaluationTotal.toLocaleString() }} evals (30d)
                    </div>
                </dd>
            </div>
            <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                <dt
                    class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                >
                    Last Updated
                </dt>
                <dd
                    class="mt-1.5 flex items-baseline justify-between md:block lg:flex"
                >
                    <div
                        class="items-baseline text-sm font-medium text-taupe-800"
                    >
                        <img
                            v-if="
                                currentTarget == null ||
                                (currentTarget != null &&
                                    currentTarget.published_at == null)
                            "
                            src="https://cdn.zenmanage.com/images/spinner-1s-200px-taupe-500.png"
                            alt="[Spinner]"
                            class="h-6 w-6"
                        />
                        <div
                            v-if="
                                currentTarget != null &&
                                currentTarget.published_at != null
                            "
                        >
                            <zen-date-time
                                :value="currentTarget.published_at"
                            />
                        </div>
                        <div
                            v-if="
                                currentTarget != null &&
                                currentTarget.user != null
                            "
                            class="text-xs font-normal text-taupe-400"
                        >
                            by
                            {{
                                currentTarget.user.data.first_name +
                                ' ' +
                                currentTarget.user.data.last_name
                            }}
                        </div>
                    </div>
                </dd>
            </div>
            <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                <dt
                    class="text-xs font-medium uppercase tracking-wider text-taupe-400 flex items-center space-x-0.5"
                >
                    <div>Default Value</div>
                    <div class="text-sky-500">
                        <zen-tooltip
                            content="If Zenmanage is unavailable, this is the value that is served. This value is defined in your code."
                            placement="bottom"
                            v-slot="{ setReference, ariaAttrs, show, hide }"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                class="h-3.5 w-3.5"
                                tabindex="0"
                                :ref="setReference"
                                v-bind="ariaAttrs"
                                @mouseenter="show"
                                @mouseleave="hide"
                                @focus="show"
                                @blur="hide"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0ZM8.94 6.94a.75.75 0 1 1-1.061-1.061 3 3 0 1 1 2.871 5.026v.345a.75.75 0 0 1-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 1 0 8.94 6.94ZM10 15a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </zen-tooltip>
                    </div>
                </dt>
                <dd
                    class="mt-1.5 flex items-center justify-between md:block lg:flex min-w-0"
                >
                    <div
                        class="items-center text-sm font-medium text-taupe-800 flex space-x-1 min-w-0"
                    >
                        <div data-testid="stat-value-default" class="min-w-0">
                            <zen-skeleton
                                v-if="loading.lastRetrieval"
                                class="h-4 w-20"
                            />
                            <div
                                v-if="
                                    !loading.lastRetrieval &&
                                    defaultValueDisplay === null
                                "
                            >
                                <em>(Not set)</em>
                            </div>
                            <div
                                v-if="
                                    !loading.lastRetrieval &&
                                    defaultValueDisplay !== null
                                "
                                class="truncate"
                                :title="defaultValueDisplay"
                            >
                                {{ defaultValueDisplay }}
                            </div>
                        </div>
                        <div
                            v-if="
                                !loading.lastUse &&
                                lastUse != undefined &&
                                lastUse.created_at != undefined &&
                                !loading.lastRetrieval &&
                                defaultValueDisplay === null
                            "
                            class="text-red-500"
                        >
                            <zen-tooltip
                                content="This flag is currently in use but there is no default value set. In the unlikely event that Zenmanage is unavailable, your application may experience unexpected difficulties."
                                placement="bottom"
                                v-slot="{ setReference, ariaAttrs, show, hide }"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    class="h-3.5 w-3.5"
                                    tabindex="0"
                                    :ref="setReference"
                                    v-bind="ariaAttrs"
                                    @mouseenter="show"
                                    @mouseleave="hide"
                                    @focus="show"
                                    @blur="hide"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </zen-tooltip>
                        </div>
                    </div>
                </dd>
            </div>
        </dl>
    </div>
</template>

<script>
import {
    arrow,
    autoUpdate,
    computePosition,
    flip,
    offset,
    shift,
} from '@floating-ui/dom'
import TargetService from '@/services/target.service'
import UsageService from '@/services/usage.service'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

export default {
    components: { ProjectChangeNotice },
    name: 'FlagStatsPanel',
    props: {
        currentTarget: {
            type: Object,
            required: false,
            default: null,
        },
        flag: {
            type: Object,
            required: true,
        },
        projectKey: {
            type: String,
            required: true,
        },
        environmentKey: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: {
                lastRetrieval: true,
                lastUse: true,
                evaluations: true,
            },
            lastRetrieval: null,
            lastUse: null,
            evaluationSeries: [],
            evaluationTotal: 0,
            evalTooltip: {
                visible: false,
                // Hidden at (0,0) until the first computePosition() resolves,
                // so the teleported tooltip never flashes at the top of
                // document.body for a frame before its real position is known.
                style: {
                    position: 'fixed',
                    top: '0px',
                    left: '0px',
                    visibility: 'hidden',
                },
                arrowStyle: {},
            },
        }
    },
    created() {
        this.stopEvalAutoUpdate = null
    },
    computed: {
        // The API returns the raw default value as plain text (or null/absent when
        // no default was reported) — there's no wrapper to unpack. null means "no
        // default reported" (Not set); a string-type flag's empty-string default is
        // a real, distinct value and gets the (empty) treatment instead.
        defaultValueDisplay() {
            const defaultValue = this.lastRetrieval?.default_value
            if (defaultValue == undefined) {
                return null
            }

            const isEmptyString =
                this.flag.type === 'string' && defaultValue === ''

            return isEmptyString ? '(empty)' : defaultValue
        },
    },
    methods: {
        refreshLastRetrieval() {
            this.loading.lastRetrieval = true
            TargetService.lastRetrieval(
                this.projectKey,
                this.environmentKey,
                this.flag.key
            )
                .then((response) => {
                    if (response !== undefined) {
                        this.lastRetrieval = response.data ?? null
                    }
                })
                .catch(() => {
                    // Non-critical panel state
                })
                .finally(() => (this.loading.lastRetrieval = false))
        },
        refreshLastUse() {
            this.loading.lastUse = true
            UsageService.lastUse(
                this.projectKey,
                this.environmentKey,
                this.flag.key
            )
                .then((response) => {
                    if (response !== undefined) {
                        this.lastUse = response.data ?? null
                    }
                })
                .catch(() => {
                    // Non-critical panel state
                })
                .finally(() => (this.loading.lastUse = false))
        },
        refreshEvaluations() {
            this.loading.evaluations = true
            UsageService.history(
                this.projectKey,
                this.environmentKey,
                this.flag.key,
                '30d',
                false
            )
                .then((response) => {
                    const payload = response?.data
                    if (payload && Array.isArray(payload.value_series)) {
                        this.evaluationSeries = payload.value_series
                        this.evaluationTotal = payload.totals?.current ?? 0
                    }
                })
                .catch(() => {
                    // Non-critical panel state
                })
                .finally(() => (this.loading.evaluations = false))
        },
        evaluationPercent(series) {
            if (this.evaluationTotal === 0) return 0
            return Math.round((series.total / this.evaluationTotal) * 100)
        },
        async showEvalTooltip() {
            this.evalTooltip.visible = true
            // Reset to hidden at (0,0) in case the bar moved since the last
            // time this was shown — avoids flashing the stale position.
            this.evalTooltip.style = {
                position: 'fixed',
                top: '0px',
                left: '0px',
                visibility: 'hidden',
            }
            await this.$nextTick()

            const referenceEl = this.$refs.evalBarRef
            const floatingEl = this.$refs.evalTooltipRef
            if (!referenceEl || !floatingEl) {
                return
            }

            await this.updateEvalTooltipPosition()
            // `strategy: 'fixed'` positions the tooltip relative to the
            // viewport rather than the bar, so without autoUpdate it would
            // detach from the bar on scroll/resize instead of tracking it.
            this.stopEvalAutoUpdate = autoUpdate(
                referenceEl,
                floatingEl,
                this.updateEvalTooltipPosition
            )
        },
        hideEvalTooltip() {
            this.evalTooltip.visible = false
            this.stopEvalAutoUpdate?.()
            this.stopEvalAutoUpdate = null
        },
        // Positions the tooltip with Floating UI at `strategy: 'fixed'` so it
        // renders relative to the viewport rather than the nearest positioned
        // ancestor — the panel's `overflow-hidden` (used for its rounded
        // corners) would otherwise clip the tooltip when it opens near the
        // panel's top edge. flip() drops it below the bar instead when there
        // isn't room above.
        async updateEvalTooltipPosition() {
            const referenceEl = this.$refs.evalBarRef
            const floatingEl = this.$refs.evalTooltipRef
            const arrowEl = this.$refs.evalArrowRef
            if (!referenceEl || !floatingEl || !arrowEl) {
                return
            }

            const { x, y, placement, middlewareData } = await computePosition(
                referenceEl,
                floatingEl,
                {
                    strategy: 'fixed',
                    placement: 'top',
                    middleware: [
                        offset(8),
                        flip(),
                        shift({ padding: 8 }),
                        arrow({ element: arrowEl }),
                    ],
                }
            )

            // The tooltip may have been hidden (or the component unmounted)
            // while this was in flight — bail out rather than show a stale
            // position.
            if (!this.$refs.evalTooltipRef || !this.$refs.evalArrowRef) {
                return
            }

            this.evalTooltip.style = {
                position: 'fixed',
                top: `${y}px`,
                left: `${x}px`,
            }

            const { x: arrowX, y: arrowY } = middlewareData.arrow || {}
            const opposedSide = {
                left: 'right',
                right: 'left',
                bottom: 'top',
                top: 'bottom',
            }[placement.split('-')[0]]

            this.evalTooltip.arrowStyle = {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                bottom: '',
                right: '',
                [opposedSide]: '-4px',
            }
        },
        // Reloads every stat. The notice calls this when the flag or its
        // targets change elsewhere and the user asks for a refresh.
        refreshStats() {
            this.refreshLastRetrieval()
            this.refreshLastUse()
            this.refreshEvaluations()
        },
    },
    mounted() {
        this.refreshStats()
    },
    beforeUnmount() {
        this.stopEvalAutoUpdate?.()
    },
}
</script>
